import { useState, useRef, useEffect } from 'react';

/**
 * useAdminDashboard Hook
 *
 * Manages all state for the AdminDashboard component
 * Extracted to reduce complexity and improve maintainability
 */
export const useAdminDashboard = () => {
  // Navigation state
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Dropdown states
  const [myPropertyOpen, setMyPropertyOpen] = useState(false);
  const [bookingsOpen, setBookingsOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Refs for dropdown menus
  const myPropertyRef = useRef(null);
  const bookingsRef = useRef(null);
  const calendarRef = useRef(null);

  // Booking dialog state
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRoomForBooking, setSelectedRoomForBooking] = useState(null);
  const [selectedDateForBooking, setSelectedDateForBooking] = useState(null);
  const [bookingViewMode, setBookingViewMode] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    room_id: '',
    check_in: '',
    check_out: '',
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guest_country: '',
    num_guests: 1,
    special_requests: '',
    payment_status: 'pending',
    car_id: null,
    car_start_date: '',
    car_end_date: ''
  });

  // Global search state
  const [globalSearchQuery, setGlobalSearchQuery] = useState('');

  // Clear search when navigating away from bookings
  useEffect(() => {
    if (activeTab !== 'bookings' && globalSearchQuery) {
      setGlobalSearchQuery('');
    }
  }, [activeTab, globalSearchQuery]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (myPropertyRef.current && !myPropertyRef.current.contains(event.target)) {
        setMyPropertyOpen(false);
      }
      if (bookingsRef.current && !bookingsRef.current.contains(event.target)) {
        setBookingsOpen(false);
      }
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Keyboard shortcut for search: Press "/" to focus
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === '/' && !['INPUT', 'TEXTAREA'].includes(event.target.tagName)) {
        event.preventDefault();
        const searchInput = document.querySelector('input[placeholder="Search bookings..."]');
        if (searchInput) {
          searchInput.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Reset booking form
  const resetBookingForm = () => {
    setSelectedBooking(null);
    setSelectedRoomForBooking(null);
    setSelectedDateForBooking(null);
    setBookingViewMode(false);
    setBookingForm({
      room_id: '',
      check_in: '',
      check_out: '',
      guest_name: '',
      guest_email: '',
      guest_phone: '',
      guest_country: '',
      num_guests: 1,
      special_requests: '',
      payment_status: 'pending',
      car_id: null,
      car_start_date: '',
      car_end_date: ''
    });
  };

  return {
    // Navigation
    activeTab,
    setActiveTab,
    mobileMenuOpen,
    setMobileMenuOpen,

    // Dropdowns
    myPropertyOpen,
    setMyPropertyOpen,
    bookingsOpen,
    setBookingsOpen,
    calendarOpen,
    setCalendarOpen,
    myPropertyRef,
    bookingsRef,
    calendarRef,

    // Booking dialog
    showBookingDialog,
    setShowBookingDialog,
    selectedBooking,
    setSelectedBooking,
    selectedRoomForBooking,
    setSelectedRoomForBooking,
    selectedDateForBooking,
    setSelectedDateForBooking,
    bookingViewMode,
    setBookingViewMode,
    bookingForm,
    setBookingForm,
    resetBookingForm,

    // Search
    globalSearchQuery,
    setGlobalSearchQuery,
  };
};
